import React, { useEffect, useState } from "react";
import { collection, deleteDoc, doc, onSnapshot } from "firebase/firestore";
import { db } from "../../../firestore-DB-config/firebase-config"; // Adjust the path based on your firebase config
import {
  Card,
  CardContent,
  CardMedia,
  Typography,
  Chip,
  Grid2,
  CircularProgress,
  Box,
  IconButton,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import Header from "../../../components/Header/Header";
import Footer from "../../../components/Footer/Footer";
import { Link } from "react-router-dom";
import BedIcon from "@mui/icons-material/KingBed";
import BathIcon from "@mui/icons-material/Bathtub";
import WhatshotIcon from "@mui/icons-material/Whatshot";
import HomeIcon from "@mui/icons-material/Home";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// TypeScript Interface for Property
interface PropertyCardProps {
  id: string;
  image: string;
  price: string;
  title: string;
  address: string;
  beds: number;
  baths: number;
  label: string;
  labelColor: "error" | "primary" | "success"; // Ensure this is defined as a union type
  labelIcon: JSX.Element;
  type: string;
}

const PropertyList: React.FC = () => {
  const [properties, setProperties] = useState<PropertyCardProps[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [open, setOpen] = useState<boolean>(false);
  const [selectedProperty, setSelectedProperty] = useState<string>("");

  // Fetch properties from Firestore (real-time updates)
  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, "properties"), (snapshot) => {
      const propertyList = snapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id, // Firestore document ID
          image: data.imageUrls || "", // Provide default values if necessary
          price: data.price || "",
          title: data.title || "",
          address: data.address || "",
          beds: data.beds || 0,
          baths: data.baths || 0,
          label: data.label || "",
          labelColor: data.labelColor,
          labelIcon: getLabelIcon(data.label), // Handle labelIcon
          type: data.type || "",
        } as PropertyCardProps;
      });
      setProperties(propertyList);
      setLoading(false); // Data is loaded, stop showing the spinner
    });

    return () => unsubscribe();
  }, []);

  // Function to open the confirmation dialog
  const handleClickOpen = (propertyId: string) => {
    setSelectedProperty(propertyId);
    setOpen(true);
  };

  // Function to close the dialog
  const handleClose = () => {
    setOpen(false);
  };

  // Confirm and delete property
  const handleConfirmDelete = async () => {
    try {
      await deleteDoc(doc(db, "properties", selectedProperty));
      toast.success("Property deleted successfully");
    } catch (error) {
      toast.error("Error deleting property");
      console.error("Error deleting property:", error);
    }
    handleClose(); // Close the dialog after the action
  };

  // Define a function to map labels to color styles
  const getLabelColor = (label: string): string => {
    switch (label) {
      case "Newest":
        return "#1976d2"; // Blue for 'Newest'
      case "Discounted":
        return "#2e7d32"; // Green for 'Discounted'
      case "Popular":
        return "#d32f2f"; // Red for 'Popular'
      default:
        return "#555555"; // Default color for unknown labels
    }
  };

  // Function to map labels to icons
  const getLabelIcon = (label: string): JSX.Element => {
    switch (label) {
      case "Newest":
        return <HomeIcon />;
      case "Discounted":
        return <AttachMoneyIcon />;
      case "Popular":
        return <WhatshotIcon />;
      default:
        return <HomeIcon />; // Default to HomeIcon if no match
    }
  };

  return (
    <>
      <Header />
      <Container sx={{ mb: "50px" }}>
        <Box sx={{ padding: 3 }}>
          <Typography
            variant="h4"
            fontWeight="bold"
            sx={{
              fontSize: { xs: "24px", md: "32px" },
              color: "rgba(1, 29, 51, 1)",
              my: "30px",
            }}
          >
            Delete Properties
          </Typography>
          {loading ? (
            <CircularProgress />
          ) : (
            <Grid2 container spacing={6}>
              {properties.length > 0 ? (
                properties.map((prp) => (
                  <Grid2 size={{ xs: 12, sm: 6, md: 4 }} key={prp.id}>
                    <Card
                      sx={{
                        width: { xs: "280px", sm: "300px", md: "320px" },
                        height: "100%",
                        borderRadius: 2,
                        boxShadow: 3,
                        transition:
                          "transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out",
                        cursor: "pointer",
                        "&:hover": {
                          transform: "scale(1.06)",
                          boxShadow: 6,
                        },
                        mx: { xs: 0, sm: 1 },
                        position: "relative",
                      }}
                    >
                      <Link
                        to={`/property/${prp.id}`}
                        style={{ textDecoration: "none", color: "inherit" }}
                        onClick={() => window.scrollTo(0, 0)}
                      >
                        <Box position="relative">
                          <CardMedia
                            component="img"
                            image={prp.image}
                            alt={prp.title}
                            sx={{
                              borderRadius: "8px 8px 0 0",
                              height: { xs: 180, sm: 220, md: 250 },
                              objectFit: "cover",
                            }}
                          />
                          <Chip
                            label={prp.label}
                            color={prp.labelColor}
                            icon={prp.labelIcon}
                            sx={{
                              position: "absolute",
                              bottom: { xs: 5, sm: 10 },
                              right: { xs: 5, sm: 10 },
                              fontSize: { xs: "0.70rem", sm: "0.875rem" },
                              color: getLabelColor(prp.label), // Dynamically set background color
                              backgroundColor: "white", // Text color is white
                              fontWeight: "bold",
                            }}
                          />
                        </Box>

                        <CardContent>
                          <Typography
                            sx={{ fontSize: { xs: "16px", md: "20px" } }}
                            color="primary"
                            fontWeight="bold"
                          >
                            {prp.price}
                          </Typography>
                          <Typography
                            sx={{ fontSize: { xs: "12px", md: "16px" } }}
                            variant="subtitle1"
                            fontWeight="bold"
                            gutterBottom
                          >
                            {prp.title}
                          </Typography>
                          <Typography
                            sx={{ fontSize: { xs: "11px", md: "15px" } }}
                            variant="body2"
                            color="text.secondary"
                            gutterBottom
                          >
                            {prp.address}
                          </Typography>
                          <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                            mt={2}
                          >
                            <Box display="flex" alignItems="center" gap={0.5}>
                              <BedIcon
                                sx={{ fontSize: { xs: "14px", md: "20px" } }}
                              />
                              <Typography
                                sx={{ fontSize: { xs: "12px", md: "16px" } }}
                                variant="body2"
                              >
                                {prp.beds} Beds
                              </Typography>
                            </Box>
                            <Box display="flex" alignItems="center" gap={0.5}>
                              <BathIcon
                                sx={{ fontSize: { xs: "14px", md: "20px" } }}
                              />
                              <Typography
                                sx={{ fontSize: { xs: "12px", md: "16px" } }}
                                variant="body2"
                              >
                                {prp.baths} Bath
                              </Typography>
                            </Box>
                          </Box>
                        </CardContent>
                      </Link>

                      {/* Delete button */}
                      <IconButton
                        sx={{
                          position: "absolute",
                          top: 10,
                          right: 10,
                          color: "error.main",
                          backgroundColor: "rgba(255, 255, 255, 0.8)",
                          borderRadius: "50%",
                          boxShadow: 1,
                          "&:hover": {
                            backgroundColor: "rgba(255, 0, 0, 0.7)",
                          },
                        }}
                        onClick={() => handleClickOpen(prp.id)}
                      >
                        <DeleteIcon sx={{ fontSize: 36 }} />
                      </IconButton>
                    </Card>
                  </Grid2>
                ))
              ) : (
                <Typography>No properties found.</Typography>
              )}
            </Grid2>
          )}
        </Box>
      </Container>
      <Footer />

      {/* Toast Container to show notifications */}
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />

      {/* Material-UI Dialog for confirmation */}
      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="confirm-delete-title"
        aria-describedby="confirm-delete-description"
      >
        <DialogTitle id="confirm-delete-title">Delete Property</DialogTitle>
        <DialogContent>
          <DialogContentText id="confirm-delete-description">
            Are you sure you want to delete this property? This action cannot be
            undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="primary">
            Cancel
          </Button>
          <Button onClick={handleConfirmDelete} color="error" autoFocus>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PropertyList;
